import React from 'react';
import { AlertCircle, Zap, CheckCircle2 } from 'lucide-react';

const getScoreLabel = (score) => {
  if (score < 10) return 'CRÍTICO';
  if (score < 14) return 'URGENTE';
  if (score < 18) return 'MEJORABLE';
  return 'SÓLIDO';
};

const getScoreBadgeClass = (score) => {
  if (score < 10) return 'bg-red-600 text-white';
  if (score < 14) return 'bg-orange-500 text-white';
  return 'bg-[#802ef8] text-white';
};

const getAnalysisBoxClass = (score) => {
  if (score < 10) return 'bg-red-950/40 border-red-500';
  if (score < 14) return 'bg-orange-950/40 border-orange-500';
  return 'bg-[#802ef8]/10 border-[#802ef8]';
};

const getAnalysisTextClass = (score) => {
  if (score < 10) return 'text-red-200';
  if (score < 14) return 'text-orange-200';
  return 'text-white';
};

const getBarColor = (score) => {
  if (score < 10) return '#ef4444';
  if (score < 14) return '#f97316';
  return 'linear-gradient(to right, #802ef8, #9b5aff)';
};

export const MoneyAlert = ({ result }) => {
  const isPerfect = result.total_score >= 60;

  if (isPerfect) {
    return (
      <div className="relative overflow-hidden rounded-lg border-4 border-green-500 bg-gradient-to-br from-green-950/60 to-emerald-950/60 p-4 sm:p-6 md:p-12">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-green-500 to-emerald-500"></div>
        </div>
        <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-green-500 via-emerald-400 to-green-500"></div>
        
        <div className="relative z-10">
          <div className="flex items-center gap-2 sm:gap-3 mb-6">
            <div className="bg-green-600/20 p-2 sm:p-3 rounded-full flex-shrink-0">
              <CheckCircle2 className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 text-green-400" />
            </div>
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-bold text-green-400"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              SISTEMA PERFECTO
            </h2>
          </div>

          <div className="bg-black/40 p-4 sm:p-6 md:p-8 rounded-md border-2 border-green-500 mb-4 sm:mb-6">
            <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-green-400 mb-2 sm:mb-3">
              60/60
            </div>
            <p className="text-base sm:text-lg md:text-xl text-white font-bold mb-3">
              Tu sistema comercial está funcionando a la perfección.
            </p>
            <p className="text-sm sm:text-base text-[#a3a3a3]">
              No estás dejando dinero sobre la mesa. Tu oferta, marketing y ventas están optimizados. No necesitas nuestra ayuda en este momento.
            </p>
          </div>

          <div className="p-3 sm:p-4 bg-green-950/30 border-l-4 border-green-500 rounded">
            <p className="text-xs sm:text-sm md:text-base text-green-200">
              <strong className="text-green-400">ENHORABUENA:</strong> Estás en el top 2% de las agencias que analizamos. Sigue haciendo lo que estás haciendo y mantén tu sistema actualizado para conservar esta ventaja competitiva.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
  <div className="relative overflow-hidden rounded-lg border-4 border-red-600 bg-gradient-to-br from-red-950/60 to-orange-950/60 p-4 sm:p-6 md:p-12">
    <div className="absolute inset-0 opacity-20">
      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-red-500 to-orange-500 animate-pulse"></div>
    </div>
    <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-red-600 via-orange-500 to-red-600 animate-pulse"></div>
    
    <div className="relative z-10">
      <div className="flex items-center gap-2 sm:gap-3 mb-4">
        <div className="bg-red-600/20 p-2 sm:p-3 rounded-full flex-shrink-0">
          <AlertCircle className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 text-red-500" />
        </div>
        <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-500 animate-pulse flex-shrink-0" />
        <h2 
          className="text-2xl sm:text-3xl md:text-4xl font-bold text-red-500"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          ALERTA CRÍTICA
        </h2>
      </div>

      <p className="text-base sm:text-xl md:text-2xl text-white font-bold mb-3 sm:mb-4">
        Estás dejando de ganar entre:
      </p>
      
      <div className="bg-black/40 p-3 sm:p-4 md:p-6 rounded-md border-2 border-red-500 mb-4 sm:mb-6">
        <div className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-red-500 mb-1 sm:mb-2">
          {result.money_left.min.toLocaleString('es-ES')}€ - {result.money_left.max.toLocaleString('es-ES')}€
        </div>
        <div className="text-sm sm:text-base md:text-lg text-red-400 font-semibold">
          CADA MES por ineficiencias en tu sistema
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        <div className="bg-red-950/40 p-3 sm:p-4 rounded-md border border-red-800">
          <div className="text-xs sm:text-sm text-[#a3a3a3] mb-1">En 1 año pierdes:</div>
          <div className="text-xl sm:text-2xl md:text-3xl font-bold text-red-400">
            {(result.money_left.max * 12).toLocaleString('es-ES')}€
          </div>
        </div>
        <div className="bg-red-950/40 p-3 sm:p-4 rounded-md border border-red-800">
          <div className="text-xs sm:text-sm text-[#a3a3a3] mb-1">En 3 años pierdes:</div>
          <div className="text-xl sm:text-2xl md:text-3xl font-bold text-red-400">
            {(result.money_left.max * 36).toLocaleString('es-ES')}€
          </div>
        </div>
      </div>

      <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-yellow-950/30 border-l-4 border-yellow-500 rounded">
        <p className="text-xs sm:text-sm md:text-base text-yellow-200">
          <strong className="text-yellow-500">URGENTE:</strong> Cada día que pasa sin arreglar esto, estás regalando {Math.round(result.money_left.max / 30).toLocaleString('es-ES')}€. Cada semana: {Math.round((result.money_left.max / 30) * 7).toLocaleString('es-ES')}€. ¿Cuánto más vas a esperar?
        </p>
      </div>

      <div className="mt-4 sm:mt-6 bg-black/40 p-4 sm:p-6 rounded-md border border-[#262626]">
        <h3 className="text-sm sm:text-base md:text-lg font-bold text-white mb-3 sm:mb-4 flex items-center gap-2">
          <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          ¿Cómo calculamos este número?
        </h3>
        <div className="space-y-3 text-xs sm:text-sm text-[#a3a3a3]">
          <p><strong className="text-white">1. Tu facturación mensual:</strong> {result.lead_data.revenue}</p>
          <p><strong className="text-white">2. Tu puntuación del sistema:</strong> {result.total_score}/60 = {Math.round((result.total_score / 60) * 100)}% de efectividad</p>
          <p><strong className="text-white">3. Ineficiencia detectada:</strong> {100 - Math.round((result.total_score / 60) * 100)}% de tu sistema tiene problemas críticos</p>
          <p><strong className="text-white">4. Cálculo conservador:</strong> Aplicamos un multiplicador basado en datos de +50 agencias con sistemas similares al tuyo</p>
          <p className="pt-3 border-t border-[#262626]">
            <strong className="text-yellow-400">IMPORTANTE:</strong> Este es un <strong>estimado conservador</strong> basado en tu perfil.
          </p>
          <ul className="ml-4 sm:ml-6 mt-2 space-y-1 text-xs list-disc">
            <li>Ticket promedio de tus clientes</li>
            <li>Competencia en tu nicho</li>
            <li>Calidad de tus leads actuales</li>
            <li>Capacidad de tu equipo comercial</li>
            <li>Velocidad de implementación de mejoras</li>
          </ul>
          <p className="pt-3 text-[#7df9c5]">
            <strong>Lo que sí sabemos con certeza:</strong> Con tu puntuación de {result.total_score}/60, hay oportunidades claras de mejora que están afectando directamente tus ingresos.
          </p>
        </div>
      </div>
    </div>
  </div>
);
};

const DiagnosticItem = ({ label, description, isPositive }) => (
  <div className="flex items-start gap-3">
    <div className={`w-2 h-2 mt-2 rounded-full flex-shrink-0 ${isPositive ? 'bg-green-500' : 'bg-red-500'}`}></div>
    <div className="flex-1">
      <p className="text-sm text-white font-medium">{label}</p>
      <p className="text-xs text-[#666666]">{description}</p>
    </div>
  </div>
);

const DIAGNOSTICS = {
  oferta: [
    { threshold: 15, label: 'Claridad de propuesta de valor', pos: 'Tu oferta está bien definida y comunica valor claramente', neg: 'Tu oferta no está lo suficientemente clara. Los prospectos no entienden por qué deberían elegirte.' },
    { threshold: 12, label: 'Diferenciación competitiva', pos: 'Te diferencias claramente de la competencia', neg: 'Estás compitiendo por precio porque no has construido una diferenciación real.' },
    { threshold: 10, label: 'ICP (Cliente Ideal) definido', pos: 'Tienes claridad sobre quién es tu cliente ideal', neg: 'Trabajas con "quien pueda pagar" — esto destruye tu margen y eficiencia.' },
  ],
  marketing: [
    { threshold: 15, label: 'Generación de oportunidades', pos: 'Tienes un sistema predecible de generación de leads', neg: 'Dependes de referidos y "suerte". No tienes un motor de oportunidades real.' },
    { threshold: 12, label: 'Flujo de reuniones', pos: 'Tienes un flujo consistente de reuniones cualificadas', neg: 'Tu agenda está vacía o llena de reuniones con prospectos que no son tu ICP.' },
    { threshold: 10, label: 'Métricas y optimización', pos: 'Mides tus canales y optimizas basado en datos', neg: 'No sabes tu CAC, tu ROI por canal ni tu show-rate. Operas a ciegas.' },
  ],
  ventas: [
    { threshold: 15, label: 'Pipeline estructurado', pos: 'Tienes un pipeline con etapas claras y criterios de avance', neg: 'Todo está en tu cabeza o en notas sueltas. No puedes ver dónde mueren tus oportunidades.' },
    { threshold: 12, label: 'Sistema de seguimiento', pos: 'Tienes secuencias de seguimiento estructuradas', neg: 'Cuando alguien dice "déjame pensarlo", lo dejas ir. El 80% de las ventas se pierden aquí.' },
    { threshold: 10, label: 'Delegación y escalabilidad', pos: 'Tu sistema de ventas puede funcionar sin ti', neg: 'Si tú no cierras, nadie cierra. Tu negocio tiene un techo: tu tiempo.' },
  ],
};

export const BlockAnalysisCard = ({ block, Icon, ScoreIcon, scoreIconData, aiAnalysis, index }) => {
  const borderClass = block.score < 10 ? 'border-red-600' : block.score < 14 ? 'border-orange-500' : 'border-[#262626]';
  const diagnostics = DIAGNOSTICS[block.key] || [];

  return (
    <div className={`bg-[#141414] border-2 rounded-md p-4 sm:p-6 hover:border-[#802ef8] transition-all ${borderClass}`}>
      <div className="flex items-start gap-3 sm:gap-4 mb-4">
        <div className="bg-[#802ef8]/10 p-2 sm:p-3 rounded-md flex-shrink-0">
          <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#802ef8]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-2">
            <h3 className="text-base sm:text-lg md:text-xl font-bold">{block.name}</h3>
            <ScoreIcon className="w-6 h-6" style={{ color: scoreIconData.color }} />
          </div>
          
          <div className="flex items-center gap-4 mb-4">
            <div className="text-3xl font-bold text-[#802ef8]">
              {block.score}<span className="text-lg text-[#666666]">/20</span>
            </div>
            <div className="flex-1">
              <div className="bg-[#262626] h-4 rounded-full overflow-hidden">
                <div
                  className="h-full transition-all duration-1000"
                  style={{ 
                    background: getBarColor(block.score),
                    width: `${(block.score / 20) * 100}%`
                  }}
                />
              </div>
            </div>
          </div>

          {aiAnalysis ? (
            <div className="space-y-4">
              <div className={`p-5 rounded-md border-2 ${getAnalysisBoxClass(block.score)}`}>
                <div className="flex items-start gap-3 mb-3">
                  <div className={`px-3 py-1 rounded-full text-xs font-bold ${getScoreBadgeClass(block.score)}`}>
                    {getScoreLabel(block.score)}
                  </div>
                </div>
                <p className={`text-base md:text-lg leading-relaxed font-medium ${getAnalysisTextClass(block.score)}`}>
                  {aiAnalysis}
                </p>
              </div>

              <div className="bg-[#0a0a0a]/80 p-5 rounded-md border border-[#262626]">
                <h4 className="text-sm font-bold text-[#a3a3a3] mb-3 uppercase tracking-wide">
                  Diagnóstico Detallado
                </h4>
                <div className="space-y-3">
                  {diagnostics.map((item) => (
                    <DiagnosticItem
                      key={item.label}
                      label={item.label}
                      description={block.score >= item.threshold ? item.pos : item.neg}
                      isPositive={block.score >= item.threshold}
                    />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-[#262626] rounded-md">
              <p className="text-sm text-[#666666]">
                Análisis en proceso... Recarga la página en unos segundos.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
